import {
	Container,
	Grid,
	Card,
	CardActionArea,
	CardMedia,
	CardContent,
	Typography,
	IconButton,
	Tooltip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Button,
	CircularProgress,
} from "@mui/material";
import Page from "../../components/Page";
import { makeStyles } from "@mui/styles";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import TemplateCreationDialog from "./TemplateCreationDialog";
import { useEffect, useState } from "react";
import axios from "../../utils/axios";
import { format } from "date-fns";
import { useSnackbar } from "notistack";

const useStyles = makeStyles((theme) => ({
	addCard: {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
	},
	card: {
		position: "relative",
		borderRadius: "10px",
		boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
		transition: "all 0.3s",
		"&:hover": {
			transform: "translateY(-3px)",
			boxShadow: "0 4px 6px rgba(0,0,0,0.2)",
		},
	},
	deleteButton: {
		position: "absolute",
		top: 8,
		right: 8,
		zIndex: 2,
		backgroundColor: "rgba(255,255,255,0.9)",
		"&:hover": {
			backgroundColor: "#fff",
		},
	},
	media: {
		height: 200,
		backgroundPosition: "center",
        display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},
	content: {
		padding: theme.spacing(2),
	},
	title: {
		fontWeight: 600,
		fontSize: "1.25rem",
		color: "#333",
	},
	detailsGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(2, 1fr)",
		gridColumnGap: theme.spacing(2),
		marginTop: theme.spacing(1),
	},
	detail: {
		fontSize: "0.75rem",
		color: "#666",
	},
}));

export default function CampaignTemplates() {
	const classes = useStyles();
	const [open, setOpen] = useState(false);
	const [templates, setTemplates] = useState([]);
	const [deleteDialog, setDeleteDialog] = useState({
		open: false,
		template: null,
	});
	const [deleting, setDeleting] = useState(false);

	const navigate = useNavigate();
	const { enqueueSnackbar } = useSnackbar();

	const handleTemplateSelection = (templateData) => {
		navigate(`/dashboard/emailEditor/${templateData._id}`);
	};

	const handleDialogOpen = () => {
		setOpen(true);
	};

	const handleDialogClose = () => {
		setOpen(false);
	};

	const handleDeleteClick = (event, template) => {
		event.stopPropagation();
		setDeleteDialog({ open: true, template });
	};

	const handleDeleteClose = () => {
		if (deleting) return;
		setDeleteDialog({ open: false, template: null });
	};

	const handleDeleteTemplate = async () => {
		if (!deleteDialog.template) return;

		setDeleting(true);
		try {
			await axios.delete(`/templates/${deleteDialog.template._id}`);
			setTemplates((prev) =>
				prev.filter((t) => t._id !== deleteDialog.template._id)
			);
			enqueueSnackbar("Template deleted successfully", { variant: "success" });
			setDeleteDialog({ open: false, template: null });
		} catch (error) {
			console.error("Failed to delete template:", error);
			enqueueSnackbar("Failed to delete the template", { variant: "error" });
		} finally {
			setDeleting(false);
		}
	};

	useEffect(() => {
		const fetchTemplates = async () => {
			try {
				// Make the GET request (assuming the endpoint is /api/templates)
				const response = await axios.get("/templates");
				if (response.data && Array.isArray(response.data)) {
					setTemplates(response.data);
				}
			} catch (error) {
				console.error("Failed to fetch templates:", error);
			}
		};

		fetchTemplates();
	}, []); // The empty dependency array ensures this effect runs once when the component mounts

	return (
		<Page>
			<Container maxWidth="lg">
				<Typography variant="h4" gutterBottom>
					Choose a Template
				</Typography>
				<Grid container spacing={4}>
					<Grid item xs={12} sm={6} md={4} lg={3}>
						<Card
							className={`${classes.card} ${classes.addCard}`}
							onClick={(e) => handleDialogOpen(true)}
						>
							<CardActionArea>
								<CardMedia className={classes.media} title={"Add Template"}>
									<AddIcon fontSize="large" />
								</CardMedia>
								<CardContent>
									<Typography gutterBottom variant="h6" component="div">
										{"Add Template"}
									</Typography>
								</CardContent>
							</CardActionArea>
						</Card>
					</Grid>

					{templates.map((template) => (
						<Grid item key={template._id} xs={12} sm={6} md={4} lg={3}>
							<Card
								className={`${classes.card} ${
									template.special && classes.addCard
								}`}
							>
								<Tooltip title="Delete template">
									<IconButton
										className={classes.deleteButton}
										size="small"
										color="error"
										onClick={(event) => handleDeleteClick(event, template)}
										aria-label={`Delete ${template.name}`}
									>
										<DeleteIcon fontSize="small" />
									</IconButton>
								</Tooltip>
								<CardActionArea onClick={() => handleTemplateSelection(template)}>
									<CardMedia
										className={classes.media}
										image={template.image}
										title={template.name}
									/>
									<CardContent className={classes.content}>
										<Typography className={classes.title} gutterBottom>
											{template.name}
										</Typography>
										<div className={classes.detailsGrid}>
											<Typography className={classes.detail}>
												Created by {template.createdBy}
												<br />
												{format(new Date(template.createdAt), "PPP")}
											</Typography>
											<Typography className={classes.detail}>
												Updated by {template.updatedBy}
												<br />
												{format(new Date(template.updatedAt), "PPP")}
											</Typography>
										</div>
									</CardContent>
								</CardActionArea>
							</Card>
						</Grid>
					))}
				</Grid>
				<TemplateCreationDialog
					open={open}
					onOpen={handleDialogOpen}
					onClose={handleDialogClose}
					onContinue={handleTemplateSelection}
				/>
				<Dialog open={deleteDialog.open} onClose={handleDeleteClose}>
					<DialogTitle>Delete Template</DialogTitle>
					<DialogContent>
						<DialogContentText>
							Are you sure you want to delete "{deleteDialog.template?.name}"?
							This action cannot be undone.
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleDeleteClose} disabled={deleting}>
							Cancel
						</Button>
						<Button
							onClick={handleDeleteTemplate}
							color="error"
							disabled={deleting}
						>
							{deleting ? <CircularProgress size={20} /> : "Delete"}
						</Button>
					</DialogActions>
				</Dialog>
			</Container>
		</Page>
	);
}
